<template>
  <div class="edit-campaign-form ec-shell">
    <notifications position="top center"></notifications>

    <!-- Legacy intro (unauthenticated/donor-facing surface only). On admin
         contexts `compact` is true, hiding this. -->
    <div v-if="!compact" class="flex flex-col center-content mb-8 edit-campaign-form__intro">
      <h2 class="font-bold text-xl mb-2 text-slate-900">Edit your fundraising page</h2>
      <p class="text-sm text-slate-600 leading-relaxed max-w-3xl">A personalized fundraising page significantly increases your chances of hitting your goal. Tell your story, share why this cause matters, and make your appeal stand out.</p>
    </div>

    <!-- ── Tab bar (folder-style) ───────────────────────────── -->
    <div class="ec-tab-bar" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.slug"
        type="button"
        :data-ec-tab="tab.slug"
        :class="['ec-tab', tabStatusClass(tab.slug), { active: activeTab === tab.slug }]"
        :aria-selected="activeTab === tab.slug"
        :aria-controls="`ec-panel-${tab.slug}`"
        :title="tabStatusLabel(tab.slug)"
        role="tab"
        @click="setTab(tab.slug)"
      >
        <span class="ec-tab__icon" v-html="tab.icon" aria-hidden="true"></span>
        <span class="ec-tab__label">
          <span class="ec-tab__title">{{ tab.label }}</span>
          <span class="ec-tab__sub">{{ tab.sub }}</span>
        </span>
        <span
          v-if="tabStatusClass(tab.slug)"
          class="ec-tab__status"
          :aria-label="tabStatusLabel(tab.slug)"
        ></span>
      </button>
    </div>

    <!-- ── BASICS panel ─────────────────────────────────────── -->
    <div
      v-show="activeTab === 'basics'"
      id="ec-panel-basics"
      class="ec-panel"
      role="tabpanel"
      aria-labelledby="ec-tab-basics"
    >
      <header class="ec-panel__header">
        <h2 class="ec-panel__title">The basics</h2>
        <p class="ec-panel__desc">A clear name and a real goal — the two things every donor sees first.</p>
      </header>

      <div class="ec-tip">
        <span class="ec-tip__icon" aria-hidden="true">💡</span>
        <div class="ec-tip__body">
          <strong>Why this matters</strong>
          Campaigns with a specific dollar goal raise <b>more</b> than open-ended ones. A goal creates urgency ("we're almost there!") and gives donors a feeling of progress. Pick a number that feels ambitious but real.
        </div>
      </div>

      <div v-if="showStandard == false" class="flex flex-col sm:flex-row -mx-2">
        <div class="w-full sm:w-1/2 md:w-1/3 px-2 mb-4 sm:mb-0">
          <label for="campaignName" class="block text-base font-bold text-black">
            Campaign Name <sup class="text-red required">*</sup>
          </label>
          <div class="mt-1">
            <input type="text" v-model="form.name" id="campaignName" autocomplete="" class="w-full block sm:text-sm rounded-md border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500">
          </div>
        </div>

        <div class="w-full sm:w-1/2 md:w-1/3 px-2 mb-4 sm:mb-0">
          <label :for="campaignLabel.toLowerCase().replace(' ', '')" class="block text-base font-bold text-black">
            {{ campaignLabel }} <sup class="text-red required">*</sup>
          </label>
          <div class="mt-1">
            <input type="text" v-model="form.business_name" :id="campaignLabel.toLowerCase().replace(' ', '')" autocomplete="" class="w-full block sm:text-sm rounded-md border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500">
          </div>
        </div>
      </div>

      <!-- Goal section — Rick's preset cards (big amount + supporter equivalent),
           plus a "Set your own goal" input. Reuses existing form.goal binding. -->
      <div class="ec-goal-section">
        <label class="ec-goal-section__label">Fundraising Goal <sup class="text-red required">*</sup></label>
        <p class="ec-goal-section__help">Pick a target — or set your own. The equivalent shows what each goal looks like in supporters.</p>

        <div class="ec-goal-cards">
          <button
            v-for="preset in goalPresets"
            :key="preset.amount"
            type="button"
            class="ec-goal-card"
            :class="{ selected: form.goal == preset.amount && !customGoal, 'ec-goal-card--recommended': preset.recommended }"
            @click="selectGoal(preset.amount)"
          >
            <span v-if="preset.recommended" class="ec-goal-card__tag">RECOMMENDED</span>
            <span class="ec-goal-card__amount">${{ preset.amount.toLocaleString() }}</span>
            <span class="ec-goal-card__equiv">
              <b>{{ preset.supporters }} {{ preset.supportersLabel }}</b>
              giving ${{ preset.each }} each
            </span>
          </button>
        </div>

        <div class="ec-goal-custom" :class="{ 'is-focused': customGoal }">
          <label for="customGoal">Set your own goal</label>
          <input
            id="customGoal"
            type="text"
            v-model="customGoal"
            @input="updateGoal"
            placeholder="$ amount"
          >
        </div>
      </div>

      <!-- Pledge-drive fields stay conditional on the campaign type. They live
           in Basics because they describe the campaign's shape. -->
      <div v-if="isPledgeDrive && isFoundation" class="ec-field-row">
        <label class="block text-base font-bold text-black">Pledge Drive End Date<sup class="text-red required">*</sup></label>
        <div class="mt-1">
          <flat-pickr v-model="form.pledge_drive_end_date" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded-md border-gray-300"></flat-pickr>
        </div>
      </div>

      <div v-if="isPledgeDrive && isFoundation" class="ec-field-row">
        <label for="campaignGoal" class="block text-base font-bold text-black">
          Pledge Drive Activity Type <sup class="text-red required">*</sup>
        </label>
        <div class="mt-1">
          <input type="text" v-model="form.pledge_drive_activity_type" class="w-full block sm:text-sm rounded-md border-gray-300">
        </div>
      </div>
    </div>

    <!-- ── STORY panel ──────────────────────────────────────── -->
    <div
      v-show="activeTab === 'story'"
      id="ec-panel-story"
      class="ec-panel"
      role="tabpanel"
      aria-labelledby="ec-tab-story"
    >
      <header class="ec-panel__header">
        <h2 class="ec-panel__title">Your story</h2>
        <p class="ec-panel__desc">This is where donors decide whether to give. Photos and a personal story do the heavy lifting.</p>
      </header>

      <div class="ec-tip">
        <span class="ec-tip__icon" aria-hidden="true">💡</span>
        <div class="ec-tip__body">
          <strong>Why this matters</strong>
          People give to <b>people</b>, not causes. Pages with team photos and a personal story raise 2–3× more than text-only pages. Show your athletes. Explain why <i>you</i> care. Make donors feel part of the journey.
        </div>
      </div>

      <div>
        <label class="block text-base font-bold text-black mb-2">Your Media Gallery</label>
        <p class="text-base sm:text-sm mb-4">Enhance your fundraising page with a media gallery! Upload images by clicking the upload button, and add videos by uploading them to YouTube and pasting the link into the video field. A vibrant media gallery makes your page more engaging and personal, helping to boost donations.</p>
        <div class="mb-6">
          <label for="youtubeUrl" class="block text-sm font-bold text-black">Add YouTube Video</label>
          <div class="mt-1 flex items-center space-x-2">
            <input type="text" name="youtubeUrl" id="youtubeUrl" v-model="youtubeUrl"
                   class="flex-1 px-3 py-3 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm border-gray-300"
                   placeholder="https://www.youtube.com/watch?v=...">
            <button type="button" @click="addYoutubeVideo" class="ec-action-btn ec-action-btn--primary">
              <i class="fas fa-video" aria-hidden="true"></i>
              <span>Add video</span>
            </button>
          </div>
        </div>
        <div class="flex flex-col md:flex-row items-start">
          <div class="w-full md:w-auto md:mr-4 mb-4 md:mb-0">
            <div class="uploader">
              <Uploader
                :server="`/campaign/update-campaign-slider?campaign_id=${this.campaign.id}`"
                :media="sliderGalleryMediaComputed"
                :location="sliderGalleryLocation"
                :max="10"
                path="/storage/media"
                :headers="{
                  'X-CSRF-TOKEN': this.token,
                }"
                @add="addMediaHandler"
                @remove="removeMediaHandler"
                ref="uploader"
              />
            </div>
          </div>
          <div class="w-full">
            <draggable
              v-model="sliderGalleryMediaComputed"
              @end="onDragEnd"
              item-key="name"
              class="grid grid-cols-2 gap-3 sm:flex sm:flex-row sm:flex-wrap sm:gap-4 mb-4"
            >
              <template #item="{ element, index }">
                <div class="media-item relative">
                  <img :src="`${sliderGalleryLocation}/${element.name}`" :alt="element.name" class="thumbnail rounded-lg w-full h-auto" />
                  <div class="tooltip absolute bg-gray-800 text-white px-2 py-1 rounded text-sm invisible opacity-0 transition-all duration-300" :class="{ 'visible opacity-100': isTooltipVisible(element.name) }">
                    {{ element.name }}
                  </div>
                  <button class="delete-btn absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center" @click="deleteMedia(index)">
                    <i class="fas fa-trash-alt"></i>
                  </button>
                  <div class="media-type-icon absolute top-2 left-2 bg-gray-800 bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center">
                    <i :class="element.type === 'video' || element.type === 'youtube' ? 'fas fa-video' : 'fas fa-image'"></i>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </div>
        <p class="text-base sm:text-sm text-gray-600">Maximum file size: 4MB. Landscape photos will look best.</p>
      </div>

      <div class="ec-section-divider"></div>

      <div>
        <label class="block text-base font-bold text-black mb-2">About your Campaign</label>
        <p class="text-base sm:text-sm mb-4">Share your story and connect with your supporters in the "About Your Campaign" section. Use the editor below to add text, images, and videos that highlight why this cause matters to you. A compelling story can inspire more donations and help your campaign stand out. Let your passion shine through and show your supporters the impact they're contributing to.</p>
        <div class="mt-1">
          <quill-editor
            ref="myQuillEditor"
            :modules="modules"
            v-model:content="form.message"
            contentType="html"
            toolbar="full"
            class="rounded-b-md border-gray-300 campaign-quill-editor"
          />
        </div>
      </div>
    </div>

    <!-- ── EVENTS panel ─────────────────────────────────────── -->
    <div
      v-show="activeTab === 'events'"
      id="ec-panel-events"
      class="ec-panel"
      role="tabpanel"
      aria-labelledby="ec-tab-events"
    >
      <header class="ec-panel__header">
        <h2 class="ec-panel__title">Events</h2>
        <p class="ec-panel__desc">Tournaments, bake sales, car washes — anywhere people can show up.</p>
      </header>

      <div class="ec-tip">
        <span class="ec-tip__icon" aria-hidden="true">💡</span>
        <div class="ec-tip__body">
          <strong>Why this matters</strong>
          Listing real events turns your page from "an ask" into "an invitation." Supporters who attend an event donate at <b>5×</b> the rate of those who only see the link. Add every event, even the small ones.
        </div>
      </div>

      <div>
        <label class="block text-base font-bold text-black mb-2">Event Listing</label>
        <p class="text-base sm:text-sm mb-4">Whether you have a single main event or multiple smaller events like bake sales or car washes, you can list them all here. Just click the ADD EVENT button and enter each event one at a time.</p>
        <div class="flex justify-center sm:justify-start mb-7">
          <add-campaign-event-button
            ref="addCampaignEventButton"
            :campaign-name="campaign.name"
            :campaign-id="campaign.id"
            @event-added="fetchCampaignEvents"
          ></add-campaign-event-button>
        </div>

        <div class="mt-6">
          <!-- Events table (small breakpoint and up) -->
          <div class="hidden sm:block">
            <div class="align-middle inline-block min-w-full border-b border-gray-200">
              <table class="min-w-full">
                <thead>
                  <tr class="border-t border-gray-200">
                    <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                    <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th class="pr-6 py-3 border-b border-gray-200 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr v-for="event in campaignEvents" :key="event.id" class="hover:bg-gray-100">
                    <td class="px-6 py-3 text-sm text-gray-500 text-left">{{ event.title }}</td>
                    <td class="px-6 py-3 text-sm text-gray-500 text-left">{{ event.begins_at ? formatDate(event.begins_at) : '&nbsp;' }}</td>
                    <td class="px-6 py-3 text-sm text-gray-500 text-left">{{ event.end_date ? formatDate(event.end_date) : '&nbsp;' }}</td>
                    <td class="px-6 py-3 text-sm text-gray-500 text-left">{{ trimLocation(event.fullAddress) }}</td>
                    <td class="pr-6">
                      <div class="relative flex justify-end items-center space-x-2">
                        <button @click="editEvent(event)" class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                          <svg class="mr-3 h-5 w-5 text-blue-500 group-hover:text-blue-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button @click="deleteEvent(event.id)" class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                          <svg class="mr-3 h-5 w-5 text-pink-500 group-hover:text-pink-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Mobile event list -->
          <div class="sm:hidden">
            <ul class="divide-y divide-gray-200">
              <li v-for="event in campaignEvents" :key="event.id" class="py-2">
                <div class="flex justify-between items-center">
                  <div class="flex-1 min-w-0 pr-2">
                    <p class="text-base sm:text-sm font-medium text-gray-900 truncate">{{ event.title }}</p>
                    <p class="text-sm sm:text-xs text-gray-500">{{ event.begins_at ? formatDate(event.begins_at) : 'No start date' }}</p>
                    <p class="text-sm sm:text-xs text-gray-500 truncate">{{ trimLocation(event.fullAddress) }}</p>
                  </div>
                  <div class="flex space-x-2">
                    <button @click="editEvent(event)" class="text-blue-500">
                      <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                    <button @click="deleteEvent(event.id)" class="text-pink-500">
                      <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- ── SHARE panel ──────────────────────────────────────── -->
    <div
      v-show="activeTab === 'share'"
      id="ec-panel-share"
      class="ec-panel"
      role="tabpanel"
      aria-labelledby="ec-tab-share"
    >
      <header class="ec-panel__header">
        <h2 class="ec-panel__title">Spread the word</h2>
        <p class="ec-panel__desc">Sharing is the single biggest predictor of campaign success. Use every channel you have.</p>
      </header>

      <div class="ec-tip">
        <span class="ec-tip__icon" aria-hidden="true">💡</span>
        <div class="ec-tip__body">
          <strong>Why this matters</strong>
          On average, supporters need to see a campaign <b>3 times</b> before they donate. Don't share once — share everywhere, and ask a few friends to share too. Each share roughly doubles your reach.
        </div>
      </div>

      <!-- 1. Copy link -->
      <div class="ec-share-block">
        <h3 class="ec-share-block__title"><span class="ec-share-block__num">1</span>Copy your campaign link</h3>
        <div class="ec-link-row">
          <input
            ref="shareUrlInput"
            type="text"
            class="ec-link-row__url"
            :value="campaignShareUrl"
            readonly
            aria-label="Campaign link"
            @focus="$event.target.select()"
          >
          <button
            type="button"
            class="ec-action-btn ec-action-btn--primary"
            :class="{ 'is-copied': justCopied }"
            @click="copyLink"
          >
            {{ justCopied ? 'Copied!' : 'Copy link' }}
          </button>
        </div>
      </div>

      <!-- 2. Social + messaging -->
      <div class="ec-share-block">
        <h3 class="ec-share-block__title"><span class="ec-share-block__num">2</span>Share on social &amp; messaging</h3>
        <div class="ec-social-grid">
          <button
            v-for="opt in shareOptions"
            :key="opt.key"
            type="button"
            :class="['ec-social-btn', 'ec-social-btn--' + opt.key]"
            @click="openShare(opt)"
          >
            <span class="ec-social-icon" v-html="opt.icon" aria-hidden="true"></span>
            <span>{{ opt.label }}</span>
          </button>
        </div>
      </div>

      <!-- 3. QR (existing CampaignQrPanel — handles its own stats + downloads) -->
      <div class="ec-share-block">
        <h3 class="ec-share-block__title"><span class="ec-share-block__num">3</span>QR code for in-person</h3>
        <CampaignQrPanel v-if="campaign && campaign.id" :campaign="campaign" />
      </div>
    </div>

    <!-- ── Sticky footer ────────────────────────────────────── -->
    <div class="ec-footer-bar">
      <div class="ec-footer-bar__status">
        <span class="ec-footer-bar__dot" aria-hidden="true"></span>
        Your changes auto-save when you click Save.
      </div>
      <div class="ec-footer-bar__actions">
        <a href="/" class="ec-action-btn ec-action-btn--cancel" aria-label="Cancel">
          <i class="fas fa-ban" aria-hidden="true"></i>
          <span>Cancel</span>
        </a>
        <button type="button" class="ec-action-btn ec-action-btn--save" @click="updateCampaign">
          <i class="fas fa-check" aria-hidden="true"></i>
          <span>{{ saving === 'Save' ? 'Save changes' : saving }}</span>
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { defineComponent } from 'vue';
import DropZone from 'dropzone-vue';
import 'dropzone-vue/dist/dropzone-vue.common.css';
import Uploader from 'vue-media-upload';
import draggable from 'vuedraggable';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { QuillEditor } from '@vueup/vue-quill';
import BlotFormatter from 'quill-blot-formatter';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import AddCampaignEventButton from './campaign-event/AddCampaignEventButton.vue';
import EditCampaignEventLink from './campaign-event/EditCampaignEventLink.vue';
import CampaignQrPanel from './qr/CampaignQrPanel.vue';
import {
  extractYoutubeID as parseYoutubeID,
  youtubeEmbedUrl,
  youtubeThumbnailUrl,
} from '../../composables/useYouTubeUrl';

// Lucide-derived tab icons. Inlined so the component has no dependency on
// a glyph library — same approach as V2SupportOnboarding's social icons.
const TAB_ICONS = {
  basics: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
  story:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h11l5 5v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><path d="M15 4v5h5"/><path d="M7 13h7M7 17h5"/></svg>',
  events: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>',
  share:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg>',
};

// Social brand glyphs for the Share tab. Same set as V2SupportOnboarding
// + WhatsApp/Instagram/SMS/Copy to round out Rick's 8-up grid.
const SOCIAL_ICONS = {
  facebook:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>',
  twitter:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  linkedin:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 1 1 8.3 6.5a1.78 1.78 0 0 1-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0 0 13 14.19a.66.66 0 0 0 0 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 0 1 2.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>',
  whatsapp:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345m-5.446 7.443h-.016c-1.77 0-3.524-.48-5.055-1.38l-.36-.214-3.75.975 1.005-3.645-.239-.375a9.869 9.869 0 0 1-1.516-5.26c0-5.445 4.455-9.885 9.942-9.885 2.654 0 5.145 1.035 7.021 2.91 1.875 1.859 2.909 4.35 2.909 6.99-.004 5.444-4.46 9.885-9.935 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 0 0 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"/></svg>',
  instagram:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>',
  email:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>',
  sms:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/></svg>',
  copy:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>',
};

export default defineComponent({
  name: 'EditCampaign',
  components: {
    draggable,
    flatPickr,
    QuillEditor,
    DropZone,
    Uploader,
    AddCampaignEventButton,
    EditCampaignEventLink,
    CampaignQrPanel
  },
  props: {
    myCampaign: {
      type: Object,
      required: false,
    },
    // When true (passed for admin contexts where the surrounding page already
    // provides a title and description), hides the in-form donor-facing intro.
    compact: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const modules = {
      name: 'blotFormatter',
      module: BlotFormatter,
      options: {/* options */}
    }
    return { modules }
  },
  data() {
    return {
      tooltip: {
        campaign: false,
        school: false,
        eventDate: false,
        campaignGoal: false,
        message: false,
        media: false
      },
      sliderGalleryLocation: '',
      sliderGalleryMedia: [],
      myFile: null,
      campaign: this.myCampaign || {},
      parentCampaign: null,
      campaignEvents: [],
      editingEvent: null,
      form: {
        name: this.myCampaign.name,
        business_name: this.myCampaign.business_name,
        goal: this.myCampaign.goal,
        hero_image_text: this.myCampaign.hero_image_text,
        pledge_drive_end_date: this.myCampaign.pledge_drive_end_date,
        pledge_drive_activity_type: this.myCampaign.pledge_drive_activity_type,
        message: this.myCampaign.message,
        ownership: this.myCampaign.ownership,
        event_date: this.myCampaign.formattedEventDate,
        campaignCards: [],
        impact_slider_slide_one_title: this.myCampaign.impact_slider_slide_one_title ?? '',
        impact_slider_slide_two_title: this.myCampaign.impact_slider_slide_two_title ?? '',
        impact_slider_slide_three_title: this.myCampaign.impact_slider_slide_three_title ?? '',
        impact_slider_slide_one_subtitle: this.myCampaign.impact_slider_slide_one_subtitle ?? '',
        impact_slider_slide_two_subtitle: this.myCampaign.impact_slider_slide_two_subtitle ?? '',
        impact_slider_slide_three_subtitle: this.myCampaign.impact_slider_slide_three_subtitle ?? '',
        impact_slider_slide_one_text: this.myCampaign.impact_slider_slide_one_text ?? '',
        impact_slider_slide_two_text: this.myCampaign.impact_slider_slide_two_text ?? '',
        impact_slider_slide_three_text: this.myCampaign.impact_slider_slide_three_text ?? '',
      },
      cards: [],
      showStandard: true,
      uploadYourHero: 'ours',
      saving: 'Save',
      hoveredThumbnail: null,
      youtubeUrl: '',
      customGoal: '',
      // ── Tab state ───────────────────────────────────────────
      activeTab: 'basics',
      tabs: [
        { slug: 'basics', label: 'Basics',     sub: 'Name & goal',      icon: TAB_ICONS.basics },
        { slug: 'story',  label: 'Your Story', sub: 'Photos & words',   icon: TAB_ICONS.story  },
        { slug: 'events', label: 'Events',     sub: 'When & where',     icon: TAB_ICONS.events },
        { slug: 'share',  label: 'Share',      sub: 'Spread the word',  icon: TAB_ICONS.share  },
      ],
      goalPresets: [
        { amount: 1500,  supporters: 60,  supportersLabel: 'friends',    each: 25  },
        { amount: 2500,  supporters: 50,  supportersLabel: 'supporters', each: 50,  recommended: true },
        { amount: 5000,  supporters: 100, supportersLabel: 'supporters', each: 50  },
        { amount: 10000, supporters: 100, supportersLabel: 'supporters', each: 100 },
      ],
      // ── Share-tab state ────────────────────────────────────
      justCopied: false,
      _copyTimeout: null,
    }
  },
  created() {
    this.getCampaign().then(() => {
      if (this.campaign && this.campaign.id) {
        this.fetchCampaignEvents();
      } else {
        console.error('Campaign not properly initialized');
        this.$notify({ text: 'Unable to load campaign data', type: 'error' });
      }
    });
  },
  async mounted() {
    if (this.campaign.parent_id) {
      await this.getParentCampaign()
    }
    this.initializeSliderGallery()
    this.initializeGoal()
    this.initializeTabFromHash()
  },
  beforeUnmount() {
    clearTimeout(this._copyTimeout)
    if (typeof window !== 'undefined') {
      window.removeEventListener('hashchange', this.initializeTabFromHash)
    }
  },
  methods: {
    // ── Tab switching ───────────────────────────────────────
    setTab(slug) {
      if (!this.tabs.some((t) => t.slug === slug)) return
      this.activeTab = slug
      // Reflect the active tab in the URL hash so deep-linking works and
      // browser back/forward navigates between tabs. Don't trigger a page
      // jump — just update the hash silently.
      if (typeof window !== 'undefined' && window.history) {
        const url = new URL(window.location.href)
        url.hash = `#${slug}`
        window.history.replaceState(null, '', url.toString())
      }
    },
    initializeTabFromHash() {
      if (typeof window === 'undefined') return
      const hash = (window.location.hash || '').replace(/^#/, '')
      if (this.tabs.some((t) => t.slug === hash)) {
        this.activeTab = hash
      }
      window.addEventListener('hashchange', this.initializeTabFromHash, { passive: true })
    },
    /**
     * Tab status pill: 'done' when the heuristic says the tab is complete,
     * 'todo' when something's required is missing. Every tab returns a
     * value so the green-check / amber-! pattern is consistent across the
     * whole row — no tab silently rendering blank.
     *
     * Rules (single source of truth — also surfaced as tooltips so users
     * can see why a tab is checked or not):
     *   • Basics  → name + goal set
     *   • Story   → 1+ photo/video OR about-text > 100 chars
     *   • Events  → 1+ event listed
     *   • Share   → always done (URL + QR + social links are auto-set
     *               on every campaign — there's nothing the user has to
     *               configure for Share to work)
     */
    tabStatusClass(slug) {
      if (slug === 'basics') return this.basicsDone ? 'done' : 'todo'
      if (slug === 'story')  return this.storyDone  ? 'done' : 'todo'
      if (slug === 'events') return this.eventsDone ? 'done' : 'todo'
      if (slug === 'share')  return this.shareDone  ? 'done' : 'todo'
      return null
    },
    /**
     * Human-readable explanation of the current pill state, shown via
     * `title=` on the tab button so hovering reveals exactly why the
     * green check is (or isn't) there.
     */
    tabStatusLabel(slug) {
      if (slug === 'basics') {
        return this.basicsDone
          ? 'Complete — name and goal are set.'
          : 'Needs your campaign name and a fundraising goal.'
      }
      if (slug === 'story') {
        return this.storyDone
          ? 'Complete — at least one photo/video or a written story (100+ chars).'
          : 'Add a photo/video or write a story of 100+ characters.'
      }
      if (slug === 'events') {
        const n = this.campaignEvents.length
        return this.eventsDone
          ? `Complete — ${n} event${n === 1 ? '' : 's'} listed.`
          : 'Add at least one event to mark this complete.'
      }
      if (slug === 'share') {
        return 'Complete — your campaign URL, QR code, and social share links are ready.'
      }
      return ''
    },

    // ── Share-tab actions ──────────────────────────────────
    async copyLink() {
      const url = this.campaignShareUrl
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url)
        } else if (this.$refs.shareUrlInput) {
          this.$refs.shareUrlInput.select()
          this.$refs.shareUrlInput.setSelectionRange(0, url.length)
          document.execCommand('copy')
        }
        this.justCopied = true
        clearTimeout(this._copyTimeout)
        this._copyTimeout = setTimeout(() => { this.justCopied = false }, 2000)
      } catch (_e) {
        if (this.$refs.shareUrlInput) this.$refs.shareUrlInput.select()
      }
    },
    openShare(opt) {
      // The "Copy link" entry in the social grid forwards to the same
      // clipboard path the dedicated Copy-link button uses, so the
      // behavior is consistent regardless of which control is clicked.
      if (opt.key === 'copy') {
        this.copyLink()
        return
      }
      // Instagram (copy: true): copy the link, then open Instagram in a new
      // tab — copy first so the clipboard write runs while this document
      // still has focus; both fire in the same user-gesture tick. A plain
      // new tab (not the 600x500 intent popup) because instagram.com is a
      // full app, and on mobile the top-level navigation lets the OS
      // universal/app link hand off to the installed app. (Previously this
      // entry fell through to the !popup branch with an empty url, which
      // reloaded the page instead of copying.)
      if (opt.copy) {
        this.copyLink()
        const win = window.open(opt.url, '_blank')
        if (win) {
          try { win.opener = null } catch (e) { /* cross-origin handle */ }
        }
        return
      }
      if (!opt.popup) {
        // mailto: / sms: — let the browser handle it.
        window.location.href = opt.url
        return
      }
      const w = 600, h = 500
      const left = Math.max(0, (window.outerWidth - w) / 2 + (window.screenX || 0))
      const top  = Math.max(0, (window.outerHeight - h) / 2 + (window.screenY || 0))
      window.open(
        opt.url,
        '_blank',
        `popup=yes,noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`
      )
    },

    trimLocation(fullAddress) {
      if (!fullAddress) return '';
      const parts = fullAddress.split(',').map(part => part.trim()).filter(Boolean);
      return parts.join(', ');
    },
    initializeGoal() {
      const predefinedGoals = [1500, 2500, 5000, 10000];
      if (!predefinedGoals.includes(parseFloat(this.form.goal))) {
        this.customGoal = this.form.goal;
      }
    },
    fetchCampaignEvents() {
      axios.get(`/campaign/${this.campaign.id}/events`)
        .then(response => {
          console.log('Fetched campaign events:', response.data);
          this.campaignEvents = response.data;
        })
        .catch(error => {
          console.error('Error fetching campaign events:', error);
          console.log('Error response:', error.response);
          this.$notify({
            text: `Failed to fetch campaign events: ${error.response?.data?.message || error.message}`,
            type: 'error'
          });
        });
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    deleteEvent(eventId) {
      if (confirm('Are you sure you want to delete this event?')) {
        axios.delete(`/campaign/${this.campaign.id}/events/${eventId}`)
          .then(response => {
            this.campaignEvents = this.campaignEvents.filter(event => event.id !== eventId);
            this.$notify({ text: 'Event deleted successfully', type: 'success' });
          })
          .catch(error => {
            console.error('Error deleting event:', error);
            this.$notify({ text: 'Failed to delete event', type: 'error' });
          });
      }
    },
    editEvent(event) {
      this.editingEvent = event;
      this.$refs.addCampaignEventButton.openModal(event);
    },
    async getParentCampaign() {
      try {
        const response = await axios.get(`/campaign/${this.campaign.parent_id}`)
        this.parentCampaign = response.data.campaign
      } catch (error) {
        console.error('Error fetching parent campaign:', error)
      }
    },
    selectGoal(amount) {
      this.form.goal = amount;
      this.customGoal = '';
    },
    updateGoal() {
      if (this.customGoal) {
        this.form.goal = parseFloat(this.customGoal);
      }
    },
    onDragEnd() {
      this.updateMediaOrder();
    },
    updateMediaOrder() {
      const formData = new FormData();
      formData.append('campaign_id', this.campaign.id);
      this.sliderGalleryMediaComputed.forEach((item, index) => {
        formData.append(`files[${index}]`, item.url);
      });
      axios.post('/campaign/update-campaign-slider', formData, {
        headers: {
          'X-CSRF-TOKEN': this.token,
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        this.$notify({ text: 'Media order updated successfully', type: 'success' });
        this.initializeSliderGallery();
      })
      .catch(error => {
        console.error('Failed to update media order:', error);
        this.$notify({ text: 'Failed to update media order', type: 'error' });
      });
    },
    /**
     * Delegates to the shared parser so this pre-flight check accepts exactly
     * what the server accepts. The old inline regex rejected /shorts/ and
     * /live/ links — the shapes a phone share sheet produces — so those never
     * even reached the server.
     */
    extractYoutubeID(url) {
      return parseYoutubeID(url);
    },
    addYoutubeVideo() {
      if (!this.youtubeUrl) {
        this.$notify({ text: 'Please enter a YouTube URL', type: 'error' });
        return;
      }
      const videoId = this.extractYoutubeID(this.youtubeUrl);
      if (!videoId) {
        this.$notify({ text: 'Invalid YouTube URL', type: 'error' });
        return;
      }
      const newMedia = {
        name: `youtube_${videoId}`,
        type: 'youtube',
        url: `https://www.youtube.com/watch?v=${videoId}`,
        // hqdefault is generated for every video; the old 0.jpg is a 480x360
        // 4:3 frame that letterboxed inside the 16:9 gallery tiles.
        thumbnailUrl: youtubeThumbnailUrl(videoId),
        videoUrl: youtubeEmbedUrl(videoId)
      };
      this.updateCampaignSlider(newMedia);
      this.youtubeUrl = '';
    },
    updateCampaignSlider(newMedia) {
      const formData = new FormData();
      formData.append('campaign_id', this.campaign.id);
      formData.append('type', newMedia.type);
      formData.append('url', newMedia.url);
      axios.post('/campaign/update-campaign-slider', formData, {
        headers: {
          'X-CSRF-TOKEN': this.token,
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        console.log('Server response:', response.data);
        if (response.data && response.data.sliderImages) {
          this.sliderGalleryMediaComputed = response.data.sliderImages.map(item => ({
            name: item.url.split('/').pop(),
            type: item.type,
            url: item.url,
            thumbnailUrl: item.thumbnailUrl,
            videoUrl: item.videoUrl
          }));
          this.$notify({ text: 'YouTube video added successfully', type: 'success' });
          this.initializeSliderGallery();
        } else {
          throw new Error('Invalid response from server');
        }
      })
      .catch(error => {
        console.error('Failed to add YouTube video:', error);
        this.$notify({ text: `Failed to add YouTube video: ${error.response?.data?.error || error.message}`, type: 'error' });
      });
    },
    toggleTooltip(tooltipKey, show) {
      this.tooltip[tooltipKey] = show;
    },
    isTooltipVisible(fileName) {
      return this.hoveredThumbnail === fileName;
    },
    deleteMedia(index) {
      if (confirm('Are you sure you want to delete this media item?')) {
        const mediaItem = this.sliderGalleryMediaComputed[index];
        axios.post('/campaign/delete-campaign-slider-image', {
          campaign_id: this.campaign.id,
          imageName: mediaItem.name,
        }, {
          headers: { 'X-CSRF-TOKEN': this.token },
        })
        .then(response => {
          this.sliderGalleryMediaComputed.splice(index, 1);
          this.$notify({ text: 'Media item deleted successfully', type: 'success' });
        })
        .catch(error => {
          console.error('Failed to delete media item:', error);
          this.$notify({ text: 'Failed to delete media item', type: 'error' });
        });
      }
    },
    addMediaHandler(addedMedia, allMedia) {
      if (!addedMedia) {
        console.error('Invalid media object:', addedMedia);
        this.$notify({ text: 'Invalid media object', type: 'error' });
        return;
      }
      const file = addedMedia.file || addedMedia;
      const isVideo = file.type.startsWith('video/');
      const formData = new FormData();
      formData.append('image', file);
      formData.append('campaign_id', this.campaign.id);
      formData.append('type', isVideo ? 'video' : 'image');
      console.log('Sending formData:', formData);
      axios.post('/campaign/update-campaign-slider', formData, {
        headers: {
          'X-CSRF-TOKEN': this.token,
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        console.log('Server response:', response.data);
        if (response.data && (response.data.url || response.data.sliderImages)) {
          if (response.data.url) {
            const newMedia = {
              name: file.name || 'Unnamed File',
              type: isVideo ? 'video' : 'image',
              url: response.data.url,
              thumbnailUrl: isVideo ? '' : response.data.url,
              videoUrl: isVideo ? response.data.url : ''
            };
            this.sliderGalleryMediaComputed.push(newMedia);
          } else if (response.data.sliderImages) {
            this.sliderGalleryMediaComputed = response.data.sliderImages;
          }
          this.$notify({ text: `Slider ${isVideo ? 'Video' : 'Image'} added successfully`, type: 'success' });
          this.initializeSliderGallery();
          if (this.$refs.uploader && typeof this.$refs.uploader.removeFile === 'function') {
            this.$refs.uploader.removeFile(file);
          }
        } else {
          throw new Error('Invalid response from server');
        }
      })
      .catch(error => {
        console.error('Failed to add media:', error);
        this.$notify({ text: `Failed to add ${isVideo ? 'video' : 'image'}: ${error.message}`, type: 'error' });
      });
    },
    removeMediaHandler(removedImage, removedMedia) {
      const campaignId = this.campaign.id;
      axios.post(`/campaign/delete-campaign-slider-image`, {
        campaign_id: campaignId,
        imageName: removedImage.name,
      }, {
        headers: { 'X-CSRF-TOKEN': this.token },
      })
      .then(response => {
        console.log('Image deletion successful:', response.data);
        this.$notify({ text: 'Slider Image deleted successfully', type: 'success' });
      })
      .catch(error => {
        console.error('Image deletion failed:', error);
        this.$notify({ text: 'Error deleting Slider Image', type: 'error' });
      });
    },
    onUploadSuccess(response) {
      console.log('Upload Success', response);
    },
    onUploadError(error) {
      console.error('Upload Error', error);
    },
    onClick() {
      if (this.open) {
        this.open()
      }
    },
    initializeSliderGallery() {
      let mediaItems = [];
      if (this.campaign.slider_images) {
        try {
          mediaItems = JSON.parse(this.campaign.slider_images);
        } catch (error) {
          console.error('Error parsing slider_images:', error);
        }
      }
      if (!Array.isArray(mediaItems) || mediaItems.length === 0) {
        if (this.parentCampaign && this.parentCampaign.slider_images) {
          try {
            mediaItems = JSON.parse(this.parentCampaign.slider_images);
          } catch (error) {
            console.error('Error parsing parent campaign slider_images:', error);
          }
        }
      }
      if (Array.isArray(mediaItems) && mediaItems.length > 0) {
        const itemWithUrl = mediaItems.find(item => item && item.url);
        if (itemWithUrl && itemWithUrl.url) {
          try {
            const url = new URL(itemWithUrl.url);
            const lastSlashIndex = url.pathname.lastIndexOf('/');
            this.sliderGalleryLocation = lastSlashIndex !== -1
              ? url.origin + url.pathname.substring(0, lastSlashIndex)
              : url.origin + url.pathname;
          } catch (error) {
            console.error('Error parsing slider image URL:', error);
            this.sliderGalleryLocation = '';
          }
        } else {
          this.sliderGalleryLocation = '';
        }
        this.sliderGalleryMedia = mediaItems
          .filter(item => item)
          .map(item => {
            const url = item.url || '';
            const fallbackUrl = item.thumbnailUrl || item.videoUrl || '';
            const nameSource = item.name || url || fallbackUrl;
            return {
              name: nameSource ? nameSource.split('/').pop() : 'media',
              type: item.type || (item.videoUrl ? 'video' : 'image'),
              url,
              thumbnailUrl: item.thumbnailUrl || url || '',
              videoUrl: item.videoUrl || ''
            };
          })
          .filter(item => item.url || item.thumbnailUrl || item.videoUrl);
      } else {
        this.sliderGalleryLocation = '';
        this.sliderGalleryMedia = [];
      }
      console.log('Initialized slider gallery:', this.sliderGalleryMedia);
    },
    getCampaign() {
      return axios.get('/campaign/' + this.campaign.id)
        .then(response => {
          console.log('Campaign data:', response.data);
          this.campaign = response.data.campaign
          this.cards = response.data.cards
          this.showStandard = response.data.showStandard
          this.form.campaignCards = response.data.campaignCards
          this.initializeSliderGallery()
          const heroImage = [
            '/img/side-out-gradient-bg-green@2x.jpg',
            '/img/side-out-gradient-bg-blue@2x.jpg',
            '/img/side-out-gradient-bg-pink@2x.jpg',
            '/img/side-out-gradient-bg@2x.jpg',
          ]
          if (!heroImage.includes(this.campaign.hero_image_path)) {
            this.uploadYourHero = 'yours'
            this.form.hero_image_text = null
          }
        })
        .catch(error => {
          console.error('Error fetching campaign:', error);
          this.$notify({ text: 'Failed to fetch campaign data', type: 'error' });
          throw error;
        });
    },
    sendingEvent(file, xhr, formData) {
      formData.append('_token', this.token);
      formData.append('campaign_id', this.campaign.id);
    },
    updateCampaign() {
      if (!this.form.name) {
        this.$notify({ text: 'Campaign Name is required', type: 'error' })
        return
      }
      if (!this.form.goal) {
        this.$notify({ text: 'Goal is required', type: 'error' })
        return
      }
      if (this.isPledgeDrive && !this.form.pledge_drive_end_date) {
        this.$notify({ text: "Pledge Drive End Date is required", type: 'error' })
        return
      }
      if (this.isPledgeDrive && !this.form.pledge_drive_activity_type) {
        this.$notify({ text: "Pledge Drive Activity Type is required", type: 'error' })
        return
      }
      this.saving = 'Updating...'
      axios.post('/update-campaign/' + this.campaign.id, this.form)
        .then(response => {
          console.log(response.data)
          if (response.data.type == 'success') {
            setTimeout(() => {
              this.getCampaign()
              this.$notify({ text: 'Campaign has been updated.', type: 'success' })
              this.saving = 'Save'
            }, 2000)
          } else {
            this.$notify({ text: response.data.message, type: 'error' })
          }
        })
    },
    sending(files, xhr, formData) {
      formData.append('_token', this.token);
      formData.append('campaign_id', this.campaign.id);
    },
    getImpressionCount(campaignCard) {
      let impressions = campaignCard.impressions
      if (impressions.length == 0) return 0;
      return impressions[0].view_count
    }
  },

  computed: {
    sliderGalleryMediaComputed: {
      get() { return this.sliderGalleryMedia; },
      set(newValue) {
        this.sliderGalleryMedia = newValue;
        this.campaign.slider_images = JSON.stringify(newValue.map(item => ({
          type: item.type || 'image',
          url: item.url,
          thumbnailUrl: item.thumbnailUrl || item.url,
          videoUrl: item.videoUrl || ''
        })));
      }
    },
    campaignLabel() {
      const ownershipMapping = {
        school: 'School Name',
        club: 'Club Name',
        clubteam: 'Club Team Name',
        business: 'Business Name',
        tournament: 'Tournament Name',
        individual: 'Individual Name',
        unset: 'Organization Name',
      };
      return ownershipMapping[this.myCampaign.ownership] || 'Organization Name';
    },
    token() {
      return $('meta[name="csrf-token"]').attr('content')
    },
    isPledgeDrive() { return this.myCampaign.isPledgeDrive },
    isFoundation() { return this.myCampaign.isFoundation },
    dragOptions() {
      return { disabled: !this.isFoundation };
    },
    // ── Tab status heuristics ──────────────────────────────
    basicsDone() {
      const hasGoal = !!this.form.goal && Number(this.form.goal) > 0
      const hasName = !!(this.form.name && String(this.form.name).trim().length > 0)
      return hasGoal && hasName
    },
    storyDone() {
      const hasPhoto = Array.isArray(this.sliderGalleryMedia) && this.sliderGalleryMedia.length > 0
      const hasAbout = !!(this.form.message && this.stripTags(this.form.message).length > 100)
      return hasPhoto || hasAbout
    },
    eventsDone() {
      return Array.isArray(this.campaignEvents) && this.campaignEvents.length > 0
    },
    shareDone() {
      // The Share tab is "complete" the moment a campaign exists — its
      // URL, QR code, and social-share links are auto-generated. Surfacing
      // a green check here keeps the row consistent (every tab has a
      // status) without forcing the user to perform a phantom action.
      return !!(this.campaign && this.campaign.id)
    },
    // ── Share URLs ─────────────────────────────────────────
    campaignShareUrl() {
      // Mirror the link your /campaigns/{id} resolver renders. Falls back
      // to the current origin during SSR-safe paths; in practice this only
      // runs in the browser so window is always defined.
      if (typeof window === 'undefined' || !this.campaign?.id) return ''
      return `${window.location.origin}/campaigns/${this.campaign.id}`
    },
    shareOptions() {
      const url = encodeURIComponent(this.campaignShareUrl)
      const campaign = this.campaign?.name || 'our campaign'
      const twitterText = encodeURIComponent(
        `Supporting ${campaign} 🏐 Every dollar funds breakthrough research for metastatic breast cancer patients. #DigPink #WeWontIgnoreStageIV`
      )
      const emailSubject = encodeURIComponent(`Will you join me in supporting ${campaign}?`)
      const emailBody = encodeURIComponent(
        `Hi,\n\nI'm supporting ${campaign} and wanted to share it with you.\n\nSide-Out Foundation funds breakthrough precision-medicine research for metastatic breast cancer — a disease that still has no cure. Every dollar raised through ${campaign} goes directly toward that research.\n\nYou can learn more and donate here: ${this.campaignShareUrl}\n\nThanks for considering it.`
      )
      const smsBody = encodeURIComponent(
        `I'm supporting ${campaign} for Side-Out Foundation — would you take a look? ${this.campaignShareUrl}`
      )
      return [
        { key: 'facebook',  label: 'Facebook',    icon: SOCIAL_ICONS.facebook,  url: `https://www.facebook.com/sharer/sharer.php?u=${url}`, popup: true  },
        { key: 'twitter',   label: 'X / Twitter', icon: SOCIAL_ICONS.twitter,   url: `https://twitter.com/intent/tweet?url=${url}&text=${twitterText}`, popup: true },
        { key: 'linkedin',  label: 'LinkedIn',    icon: SOCIAL_ICONS.linkedin,  url: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`, popup: true },
        { key: 'whatsapp',  label: 'WhatsApp',    icon: SOCIAL_ICONS.whatsapp,  url: `https://wa.me/?text=${encodeURIComponent(this.campaignShareUrl)}`, popup: true },
        // Instagram doesn't expose a web-share endpoint that accepts a URL,
        // so the button copies the link (ready to paste into a Story/DM) AND
        // opens Instagram itself (Rick 2026-06-11: the button must actually
        // open Instagram, not just copy).
        { key: 'instagram', label: 'Instagram',   icon: SOCIAL_ICONS.instagram, url: 'https://www.instagram.com/', popup: false, copy: true },
        { key: 'email',     label: 'Email',       icon: SOCIAL_ICONS.email,     url: `mailto:?subject=${emailSubject}&body=${emailBody}`, popup: false },
        { key: 'sms',       label: 'Text / SMS',  icon: SOCIAL_ICONS.sms,       url: `sms:?&body=${smsBody}`, popup: false },
        { key: 'copy',      label: 'Copy link',   icon: SOCIAL_ICONS.copy,      url: '', popup: false },
      ]
    },
  },
  // Defined outside computed so Vue treats it as a plain helper, not a
  // reactive dependency.
  beforeCreate() {
    this.stripTags = (html) => String(html || '').replace(/<[^>]*>/g, '').trim()
  },
})
</script>

<style>
  /* ════ Tab shell ═══════════════════════════════════════════════════════ */
  .ec-shell {
    --ec-pink: #DA1884;
    --ec-pink-deep: #BC0E65;
    --ec-pink-light: #FCE4F1;
    --ec-pink-tint: #FFF5FA;
    --ec-ink: #1A1A2E;
    --ec-ink-soft: #2D2D44;
    --ec-muted: #6B7280;
    --ec-line: #E5E7EB;
    --ec-line-soft: #F1F3F5;
    --ec-card: #FFFFFF;
    --ec-green: #7DC242;
    --ec-green-deep: #6BA838;
    --ec-red: #E53935;
    --ec-red-deep: #C62828;
    --ec-tip-bg: #FFF8E8;
    --ec-tip-border: #F59E0B;
    --ec-tip-ink: #78350F;
    font-family: 'Red Hat Text', system-ui, -apple-system, sans-serif;
    padding-bottom: 100px; /* footer-bar clearance */
  }

  /* Tab bar — folder-style. Each button sits on a grey shelf; the active
     one becomes the same colour as the panel below so they read as one
     surface, with a pink top accent that signals "this is the open tab". */
  .ec-shell .ec-tab-bar {
    display: flex;
    gap: 4px;
    margin-bottom: 0;
    position: relative;
    z-index: 2;
  }
  .ec-shell .ec-tab {
    flex: 1;
    background: #EBEDF0;
    border: 0;
    padding: 14px 16px;
    cursor: pointer;
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: var(--ec-muted);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: background-color 0.18s ease, color 0.18s ease;
    position: relative;
    min-width: 0;
    border-radius: 0;
  }
  .ec-shell .ec-tab:hover {
    background: #DFE2E6;
    color: var(--ec-ink-soft);
  }
  .ec-shell .ec-tab.active {
    background: var(--ec-card);
    color: var(--ec-pink);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }
  .ec-shell .ec-tab.active::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: var(--ec-pink);
    border-radius: 14px 14px 0 0;
  }
  .ec-shell .ec-tab__icon {
    width: 22px; height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: currentColor;
  }
  .ec-shell .ec-tab__icon svg {
    width: 22px; height: 22px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .ec-shell .ec-tab__label {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.15;
    min-width: 0;
  }
  .ec-shell .ec-tab__sub {
    font-family: 'Red Hat Text', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--ec-muted);
    margin-top: 3px;
    letter-spacing: 0;
  }
  .ec-shell .ec-tab.active .ec-tab__sub {
    color: var(--ec-pink);
    opacity: 0.7;
  }
  .ec-shell .ec-tab__status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px; height: 18px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 800;
    margin-left: 8px;
    flex-shrink: 0;
  }
  .ec-shell .ec-tab.done .ec-tab__status {
    background: var(--ec-green);
    color: #fff;
  }
  .ec-shell .ec-tab.done .ec-tab__status::before { content: '\2713'; }
  .ec-shell .ec-tab.todo .ec-tab__status {
    background: #FEF3C7;
    color: #92400E;
    border: 1.5px solid #F59E0B;
  }
  .ec-shell .ec-tab.todo .ec-tab__status::before { content: '!'; }

  /* Panel — sits flush against the active tab so the connection reads as a
     real folder. The card-card surface inside DigPinkCampaignPageV2 uses a
     similar rounded-bottom shape. */
  .ec-shell .ec-panel {
    background: var(--ec-card);
    border-radius: 0 0 14px 14px;
    padding: 40px 44px;
    min-height: 540px;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.04),
      0 10px 30px rgba(0, 0, 0, 0.04);
    position: relative;
    z-index: 1;
  }
  .ec-shell .ec-panel__header { margin-bottom: 28px; }
  .ec-shell .ec-panel__title {
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: var(--ec-ink);
    margin: 0;
  }
  .ec-shell .ec-panel__desc {
    color: var(--ec-muted);
    margin: 6px 0 0;
    font-size: 15px;
  }

  /* "Why this matters" tip — yellow inset block. Repeats on every panel. */
  .ec-shell .ec-tip {
    background: var(--ec-tip-bg);
    border-left: 4px solid var(--ec-tip-border);
    padding: 16px 22px;
    border-radius: 8px;
    margin-bottom: 32px;
    font-size: 14px;
    color: var(--ec-tip-ink);
    display: flex;
    gap: 14px;
  }
  .ec-shell .ec-tip__icon { font-size: 22px; line-height: 1; margin-top: -2px; }
  .ec-shell .ec-tip__body strong {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-family: 'Red Hat Display', system-ui, sans-serif;
  }

  .ec-shell .ec-field-row { margin-top: 24px; }
  .ec-shell .ec-section-divider {
    height: 1px;
    background: var(--ec-line);
    margin: 32px 0;
  }

  /* ════ Goal preset cards ═══════════════════════════════════════════════ */
  .ec-shell .ec-goal-section { margin-top: 24px; }
  .ec-shell .ec-goal-section__label {
    display: block;
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    color: var(--ec-ink);
  }
  .ec-shell .ec-goal-section__help {
    font-size: 13px;
    color: var(--ec-muted);
    margin: 6px 0 16px;
  }
  .ec-shell .ec-goal-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 16px;
  }
  .ec-shell .ec-goal-card {
    background: #ffffff;
    border: 2px solid var(--ec-line);
    border-radius: 14px;
    padding: 20px 18px;
    cursor: pointer;
    text-align: left;
    font-family: 'Red Hat Display', system-ui, sans-serif;
    position: relative;
    overflow: hidden;
    transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 116px;
  }
  /* Recommended cards: push content below the corner ribbon so it never
     overlaps the dollar amount. */
  .ec-shell .ec-goal-card--recommended { padding-top: 30px; }
  .ec-shell .ec-goal-card:hover {
    border-color: var(--ec-pink-light);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(218, 24, 132, 0.08);
  }
  .ec-shell .ec-goal-card.selected {
    border-color: var(--ec-pink);
    background: linear-gradient(135deg, #fff 0%, var(--ec-pink-tint) 100%);
    box-shadow: 0 8px 24px rgba(218, 24, 132, 0.15);
  }
  .ec-shell .ec-goal-card.selected::after {
    content: '\2713';
    position: absolute;
    top: 12px; right: 12px;
    width: 22px; height: 22px;
    background: var(--ec-pink);
    color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
  }
  .ec-shell .ec-goal-card__amount {
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-size: 28px;
    font-weight: 800;
    color: var(--ec-ink);
    line-height: 1;
  }
  .ec-shell .ec-goal-card.selected .ec-goal-card__amount { color: var(--ec-pink); }
  .ec-shell .ec-goal-card__equiv {
    font-size: 12px;
    color: var(--ec-muted);
    line-height: 1.35;
    font-family: 'Red Hat Text', system-ui, sans-serif;
  }
  .ec-shell .ec-goal-card__equiv b {
    color: var(--ec-ink-soft);
    font-weight: 700;
  }
  .ec-shell .ec-goal-card__tag {
    position: absolute;
    top: -1px;
    left: -1px;
    background: var(--ec-pink);
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 14px 0 8px 0;
    letter-spacing: 0.08em;
    font-family: 'Red Hat Display', system-ui, sans-serif;
  }

  .ec-shell .ec-goal-custom {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    background: var(--ec-line-soft);
    border-radius: 12px;
    border: 1.5px solid transparent;
    transition: border-color 0.15s, background 0.15s;
  }
  .ec-shell .ec-goal-custom:focus-within,
  .ec-shell .ec-goal-custom.is-focused {
    border-color: var(--ec-pink);
    background: #fff;
  }
  .ec-shell .ec-goal-custom label {
    font-size: 13px;
    font-weight: 700;
    color: var(--ec-muted);
    font-family: 'Red Hat Display', system-ui, sans-serif;
    margin: 0;
    white-space: nowrap;
  }
  .ec-shell .ec-goal-custom input {
    flex: 1;
    border: 0;
    background: transparent;
    font-size: 18px;
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-weight: 700;
    padding: 0;
  }
  .ec-shell .ec-goal-custom input:focus {
    outline: none;
    box-shadow: none;
  }

  /* ════ Share tab ═══════════════════════════════════════════════════════ */
  .ec-shell .ec-share-block { margin-bottom: 32px; }
  .ec-shell .ec-share-block:last-child { margin-bottom: 0; }
  .ec-shell .ec-share-block__title {
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--ec-ink);
    margin: 0 0 14px;
    display: flex;
    align-items: center;
  }
  .ec-shell .ec-share-block__num {
    display: inline-flex;
    width: 22px; height: 22px;
    background: var(--ec-pink-light);
    color: var(--ec-pink);
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    margin-right: 10px;
  }

  .ec-shell .ec-link-row {
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--ec-line-soft);
    border-radius: 10px;
    padding: 8px 8px 8px 16px;
  }
  .ec-shell .ec-link-row__url {
    flex: 1;
    background: transparent;
    border: 0;
    font-family: 'Red Hat Text', system-ui, sans-serif;
    font-size: 13px;
    color: var(--ec-ink-soft);
    padding: 8px 0;
    outline: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  /* ec-action-btn: unified site button. Mirrors CampaignQrPanel's
     qr-panel__btn — the canonical V2 pattern (pill, Red Hat Display,
     font-weight 600, mixed case, 12-24 padding, no shadow, no gradient,
     8px icon gap). All variants inherit shape + typography from the
     base; only `background-color` changes per variant. Save/Cancel keep
     their semantic green/red colors per Rick's mockup but adopt the same
     shape so every action on the page reads as one family. */
  .ec-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1;
    border-radius: 9999px;
    border: 0;
    color: #ffffff;
    cursor: pointer;
    text-decoration: none;
    text-transform: none;
    box-shadow: none;
    white-space: nowrap;
    transition: background-color 0.15s ease, transform 0.15s ease;
  }
  .ec-action-btn:active { transform: scale(0.98); }
  .ec-action-btn:focus-visible {
    outline: 2px solid rgba(0, 0, 0, 0.18);
    outline-offset: 3px;
  }
  .ec-action-btn--primary { background-color: #DA1884; }
  .ec-action-btn--primary:hover { background-color: #BC0E65; }
  .ec-action-btn--primary.is-copied { background-color: #7DC242; }
  .ec-action-btn--save { background-color: #7DC242; }
  .ec-action-btn--save:hover { background-color: #6BA838; }
  .ec-action-btn--cancel { background-color: #E53935; }
  .ec-action-btn--cancel:hover { background-color: #C62828; }

  .ec-shell .ec-social-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }
  .ec-shell .ec-social-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border: 1.5px solid var(--ec-line);
    background: #fff;
    border-radius: 10px;
    cursor: pointer;
    font-family: 'Red Hat Display', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    color: var(--ec-ink);
    transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  }
  .ec-shell .ec-social-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    border-color: transparent;
  }
  .ec-shell .ec-social-icon {
    width: 28px; height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
  }
  .ec-shell .ec-social-icon svg { width: 16px; height: 16px; fill: currentColor; color: #fff; }
  .ec-shell .ec-social-btn--facebook  .ec-social-icon { background: #1877F2; }
  .ec-shell .ec-social-btn--twitter   .ec-social-icon { background: #000000; }
  .ec-shell .ec-social-btn--linkedin  .ec-social-icon { background: #0A66C2; }
  .ec-shell .ec-social-btn--whatsapp  .ec-social-icon { background: #25D366; }
  .ec-shell .ec-social-btn--instagram .ec-social-icon { background: linear-gradient(135deg, #F09433, #E6683C, #DC2743, #CC2366, #BC1888); }
  .ec-shell .ec-social-btn--email     .ec-social-icon { background: var(--ec-muted); }
  .ec-shell .ec-social-btn--sms       .ec-social-icon { background: #5856D6; }
  .ec-shell .ec-social-btn--copy      .ec-social-icon { background: var(--ec-ink); }

  /* ════ Sticky footer ═══════════════════════════════════════════════════ */
  .ec-shell .ec-footer-bar {
    position: sticky;
    bottom: 0;
    z-index: 20;
    background: #fff;
    border-radius: 14px;
    padding: 16px 28px;
    margin-top: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.06);
    gap: 12px;
    flex-wrap: wrap;
  }
  .ec-shell .ec-footer-bar__status {
    font-size: 13px;
    color: var(--ec-muted);
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .ec-shell .ec-footer-bar__dot {
    display: inline-block;
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--ec-green);
    box-shadow: 0 0 0 3px rgba(125, 194, 66, 0.18);
  }
  .ec-shell .ec-footer-bar__actions { display: flex; gap: 10px; }
  /* (Footer Save/Cancel + every other action button on this page use
     the shared .ec-action-btn defined below the .ec-shell scope.) */

  /* ════ Mobile ═══════════════════════════════════════════════════════════ */
  @media (max-width: 768px) {
    .ec-shell .ec-panel { padding: 24px 20px; }
    .ec-shell .ec-goal-cards { grid-template-columns: 1fr; }
    .ec-shell .ec-goal-card { min-height: 0; padding: 16px; gap: 4px; }
    .ec-shell .ec-goal-card--recommended { padding-top: 28px; }
    .ec-shell .ec-social-grid { grid-template-columns: 1fr 1fr; }
    .ec-shell .ec-tab {
      flex-direction: column;
      gap: 6px;
      padding: 12px 6px;
      font-size: 13px;
      text-align: center;
    }
    .ec-shell .ec-tab__label { align-items: center; }
    .ec-shell .ec-tab__sub { display: none; }
    .ec-shell .ec-tab__icon,
    .ec-shell .ec-tab__icon svg { width: 20px; height: 20px; }
    .ec-shell .ec-tab__status { margin-left: 4px; }
    .ec-shell .ec-footer-bar { padding: 14px 18px; }
    .ec-action-btn { padding: 11px 18px; font-size: 13px; }
  }

  /* ════ Preserved legacy styles (gallery, quill mobile rules) ═══════════ */
  .dropzone { border: none !important; }
  .media-item {
    cursor: move;
    position: relative;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    border-radius: 8px;
    background: #f1f3f5;   /* clean tile behind broken/loading images */
  }
  .thumbnail {
    width: 100%; height: 100%;
    object-fit: cover;
    font-size: 0;          /* suppress alt-text glyphs when an image 404s */
    border-radius: 8px;
    transition: transform 0.3s ease;
  }
  .media-item:hover .thumbnail { transform: scale(1.05); }
  .delete-btn { opacity: 0; transition: opacity 0.3s ease; }
  .media-item:hover .delete-btn { opacity: 1; }
  .tooltip { pointer-events: none; z-index: 10; }
  .sortable-drag .delete-btn,
  .sortable-drag .media-type-icon { display: none; }
  .mu-image-container { display: none !important; }
  .media-type-icon { opacity: 0; transition: opacity 0.3s ease; }
  .media-item:hover .media-type-icon { opacity: 1; }
  .uploader .mu-elements-wraper > .mu-plusbox-container { margin: 0 !important; }
  .uploader .mu-container {
    background: none !important;
    border: none !important;
  }
  @media (min-width: 641px) {
    .uploader, .draggable { max-width: 100%; }
    .uploader .mu-plusbox-container { width: 120px; height: 120px; }
    .media-item { width: 120px; height: 120px; }
    .draggable { gap: 10px; }
  }
  @media (max-width: 640px) {
    .delete-btn, .media-type-icon { opacity: 1; }
    .draggable { gap: 20px; }
    .uploader { display: flex; justify-content: center; }
  }

  @media (max-width: 768px) {
    .campaign-quill-editor .ql-editor {
      font-size: 1.125rem !important;
      line-height: 1.75 !important;
      min-height: 200px !important;
      padding: 1rem !important;
    }
    .campaign-quill-editor .ql-editor p {
      font-size: 1.125rem !important;
      line-height: 1.75 !important;
      margin-bottom: 1rem !important;
    }
    .campaign-quill-editor .ql-editor h1,
    .campaign-quill-editor .ql-editor h2,
    .campaign-quill-editor .ql-editor h3 {
      line-height: 1.3 !important;
      margin-bottom: 1rem !important;
    }
    .campaign-quill-editor .ql-editor ul,
    .campaign-quill-editor .ql-editor ol,
    .campaign-quill-editor .ql-editor li {
      font-size: 1.125rem !important;
      line-height: 1.75 !important;
    }
    .campaign-quill-editor .ql-editor.ql-blank::before { font-size: 1.125rem !important; }
    .campaign-quill-editor .ql-editor p:has(br:only-child) { display: none !important; }
    .campaign-quill-editor .ql-editor p { min-height: 0 !important; }
    .campaign-quill-editor .ql-editor p:empty,
    .campaign-quill-editor .ql-editor p:has(> br:first-child:last-child) { display: none !important; }
    .campaign-quill-editor .ql-editor p br:only-child { display: none !important; }
    .campaign-quill-editor .ql-editor br { line-height: 0.5 !important; }
  }
</style>
